import React from "react";
import { svg } from "../../../../../Assets/svg";
import { Modal } from "antd";
import { RootState, useAppSelector } from "../../../../../store";
import { BtnLoader } from "../../../../../Components/Components";

interface ProductionSlotConflictModalProps {
  visible: boolean;
  warningData: {
    code?: string;
    title?: string;
    message?: string;
    slot?: string;
    next_available_slot?: string;
    target_week?: number;
    conflicting_order_id?: string;
    conflicting_display_order_id?: string;
    display_order_id?: string;
    current_order_id?: string;
    current_display_order_id?: string;
    total_order_value?: number;
    weekly_target?: number;
    [key: string]: any;
  } | null;
  currentOrderNumber?: string;
  onClose: () => void;
  onConfirm: () => void;
}

const ProductionSlotConflictModal: React.FC<ProductionSlotConflictModalProps> = ({
  visible,
  warningData,
  currentOrderNumber,
  onClose,
  onConfirm,
}) => {
  const updateProductionDateLoading = useAppSelector(
    (state: RootState) => state.loading.updateProductionDateLoading,
  );

  const isWeeklyTargetExceeded =
    warningData?.code === "WEEKLY_TARGET_EXCEEDED" ||
    warningData?.weekly_target !== undefined ||
    (warningData?.message?.toLowerCase().includes("target") &&
      warningData?.message?.toLowerCase().includes("exceeded"));

  const modalTitle =
    warningData?.title ||
    (isWeeklyTargetExceeded
      ? "Weekly Target Reached"
      : "Production Slot Overlap");

  const slot = warningData?.slot;
  const targetWeek = warningData?.target_week;
  const nextSlot = warningData?.next_available_slot;
  const conflictingOrderId =
    warningData?.conflicting_display_order_id ||
    warningData?.display_order_id ||
    warningData?.conflicting_order_id;
  const currentOrderId =
    warningData?.current_display_order_id ||
    currentOrderNumber ||
    warningData?.current_order_id;

  const renderDescription = () => {
    if (slot && targetWeek && nextSlot && conflictingOrderId) {
      return (
        <span>
          Slot {slot} is already assigned to order{" "}
          <strong className="text-dark">#{conflictingOrderId}</strong> in Week{" "}
          {targetWeek}. Next available slot is {nextSlot}. Confirming will move
          the existing order{" "}
          <strong className="text-dark">#{conflictingOrderId}</strong> to slot{" "}
          {nextSlot} and assign slot {slot} to this order
          {currentOrderId ? (
            <>
              {" "}
              <strong className="text-dark">#{currentOrderId}</strong>
            </>
          ) : (
            ""
          )}
          .
        </span>
      );
    }

    // Fallback: parse message and bold all '#<orderId>' references
    const rawMessage =
      warningData?.message ||
      "Production slot is already assigned in this week.";
    const parts = rawMessage.split(/(#\S+)/g);
    return (
      <span>
        {parts.map((part, index) =>
          part.startsWith("#") ? (
            <strong key={index} className="text-dark">
              {part}
            </strong>
          ) : (
            part
          ),
        )}
      </span>
    );
  };

  return (
    <Modal
      title={
        <div className="text-center fs-4 fw-bolder lh-m">
          {modalTitle}
        </div>
      }
      open={visible}
      visible={visible}
      onCancel={onClose}
      width={"34rem"}
      closeIcon={svg.closeIcon}
      centered
      footer={
        <div className="d-flex justify-content-center gap-4">
          <button
            type="button"
            className="btn btn-lg btn-outline-secondary min-w-130"
            onClick={onClose}
            disabled={updateProductionDateLoading}
          >
            {isWeeklyTargetExceeded ? "NO" : "CANCEL"}
          </button>
          <button
            type="button"
            disabled={updateProductionDateLoading}
            className="btn btn-lg min-w-130 btn-primary"
            onClick={onConfirm}
          >
            {updateProductionDateLoading ? (
              <BtnLoader />
            ) : isWeeklyTargetExceeded ? (
              "YES"
            ) : (
              "CONFIRM"
            )}
          </button>
        </div>
      }
      className="commonModalClass"
    >
      <div className="modal-body page-form">
        <p className="fs-6 text-center text-4a4a4a mb-0 lh-base">
          {renderDescription()}
        </p>
      </div>
    </Modal>
  );
};

export default ProductionSlotConflictModal;
